import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Card, MetricCard, SectionTitle, Spinner, ErrorMsg, fmt, fmtPct, curSym } from './UI';
import { useFinancials, useInsights } from '../hooks/useData';
import { api } from '../api';

function FundRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1a2030' }}>
      <span style={{ fontSize: 13, color: '#94a3b8' }}>{label}</span>
      <span style={{ fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", color: color || '#e2e8f0', fontWeight: 500 }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

export default function FundamentalPanel({ ticker, market, quoteData }) {
  const { data: fin, loading, error } = useFinancials(ticker, market);
  const { data: insights } = useInsights(ticker, market);
  const [earnings, setEarnings] = React.useState(null);
  const currency = quoteData?.currency || 'EUR';
  const cs = curSym(currency);

  React.useEffect(() => {
    if (!ticker) return;
    setEarnings(null);
    api.earnings(ticker, market).then(setEarnings).catch(() => {});
  }, [ticker, market]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} />;

  const q = quoteData || {};
  const a = fin?.annual || {};
  const fv = insights?.fair_value;
  const protips = insights?.protips || [];

  const qData = fin?.quarterly?.map(qt => ({
    period: qt.period?.slice(0, 7),
    revenue: qt.revenue ? Math.round(qt.revenue / 1e6) : null,
    netIncome: qt.net_income ? Math.round(qt.net_income / 1e6) : null,
  })).filter(d => d.revenue).reverse() || [];

  const fvColor = fv?.verdict === 'sottovalutato' ? '#10b981' : fv?.verdict === 'sopravvalutato' ? '#f87171' : '#fbbf24';

  return (
    <div>
      {/* Fair Value + ProTips + Earnings */}
      {(fv?.available || protips.length > 0 || earnings?.earnings_date) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <Card>
            <SectionTitle>Fair Value stimato</SectionTitle>
            {fv?.available ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: fvColor, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {cs}{fv.blended}
                  </span>
                  {fv.upside_pct != null && (
                    <span style={{ fontSize: 14, fontWeight: 600, color: fvColor }}>
                      {fv.upside_pct > 0 ? '+' : ''}{fv.upside_pct}% vs prezzo
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
                  Giudizio: <span style={{ color: fvColor, fontWeight: 600 }}>{fv.verdict}</span> (prezzo attuale {cs}{fv.price})
                </div>
                {fv.estimates.map((e, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', padding: '4px 0' }}>
                    <span>{e.method}</span>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#94a3b8' }}>{cs}{e.value}</span>
                  </div>
                ))}
                <div style={{ fontSize: 10, color: '#334155', marginTop: 8, lineHeight: 1.4 }}>
                  {fv.assumptions} Stima indicativa, non un target né un consiglio.
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#475569' }}>
                Dati insufficienti per stimare il fair value (servono utili e cash flow positivi).
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle>ProTips automatici</SectionTitle>
            {earnings?.earnings_date && (
              <div style={{ fontSize: 12, color: '#60a5fa', marginBottom: 10, padding: '6px 10px', background: '#0f1e33', borderRadius: 6 }}>
                📅 Prossima trimestrale: <strong>{earnings.earnings_date}</strong>
              </div>
            )}
            {protips.length === 0 && <div style={{ fontSize: 13, color: '#475569' }}>Nessuna osservazione rilevante.</div>}
            {protips.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', fontSize: 13 }}>
                <span style={{ color: t.type === 'pos' ? '#10b981' : '#fbbf24', flexShrink: 0 }}>{t.type === 'pos' ? '✓' : '⚠'}</span>
                <span style={{ color: '#94a3b8' }}>{t.text}</span>
              </div>
            ))}
          </Card>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        <MetricCard label="P/E Ratio"       value={q.pe_ratio?.toFixed(1) ?? '—'}    sub="Trailing" />
        <MetricCard label="P/E Forward"     value={q.forward_pe?.toFixed(1) ?? '—'}  sub="Atteso" />
        <MetricCard label="EV/EBITDA"       value={q.ev_ebitda?.toFixed(1) ?? '—'} />
        <MetricCard label="P/Book"          value={q.price_to_book?.toFixed(2) ?? '—'} />
        <MetricCard label="Dividend Yield"  value={q.dividend_yield ? `${q.dividend_yield}%` : '—'} valueColor="#10b981" />
        <MetricCard label="ROE"             value={q.roe ? `${q.roe}%` : '—'} valueColor={q.roe > 10 ? '#10b981' : '#e2e8f0'} />
        <MetricCard label="Beta"            value={q.beta?.toFixed(2) ?? '—'} sub="vs FTSE MIB" />
        <MetricCard label="Debt/Equity"     value={q.debt_to_equity ? (q.debt_to_equity / 100).toFixed(2) : '—'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <Card>
          <SectionTitle>Conto Economico (TTM)</SectionTitle>
          <FundRow label="Ricavi"         value={a.revenue    ? `${cs} ${fmt(a.revenue)}` : '—'} />
          <FundRow label="Utile lordo"    value={a.gross_profit ? `${cs} ${fmt(a.gross_profit)}` : '—'} />
          <FundRow label="EBITDA"         value={a.ebitda     ? `${cs} ${fmt(a.ebitda)}` : '—'} />
          <FundRow label="Reddito oper."  value={a.operating_income ? `${cs} ${fmt(a.operating_income)}` : '—'} />
          <FundRow label="Utile netto"    value={a.net_income ? `${cs} ${fmt(a.net_income)}` : '—'} color={a.net_income > 0 ? '#10b981' : '#f87171'} />
          <FundRow label="EPS"            value={q.eps ? `${cs} ${q.eps?.toFixed(3)}` : '—'} />
          <FundRow label="Free Cash Flow" value={a.free_cashflow ? `${cs} ${fmt(a.free_cashflow)}` : q.free_cash_flow ? `${cs} ${fmt(q.free_cash_flow)}` : '—'} color="#10b981" />
        </Card>

        <Card>
          <SectionTitle>Stato Patrimoniale</SectionTitle>
          <FundRow label="Market Cap"    value={q.market_cap ? `${cs} ${fmt(q.market_cap)}` : '—'} />
          <FundRow label="Attivo totale" value={a.total_assets ? `${cs} ${fmt(a.total_assets)}` : '—'} />
          <FundRow label="Debito totale" value={a.total_debt ? `${cs} ${fmt(a.total_debt)}` : '—'} color={a.total_debt > 0 ? '#fbbf24' : '#10b981'} />
          <FundRow label="Equity"        value={a.stockholders_equity ? `${cs} ${fmt(a.stockholders_equity)}` : '—'} />
          <FundRow label="CF operativo"  value={a.operating_cashflow ? `${cs} ${fmt(a.operating_cashflow)}` : '—'} />
          <FundRow label="52w High"      value={q['52w_high'] ? `${cs} ${q['52w_high']?.toFixed(3)}` : '—'} />
          <FundRow label="52w Low"       value={q['52w_low']  ? `${cs} ${q['52w_low']?.toFixed(3)}` : '—'} />
        </Card>
      </div>

      {qData.length > 0 && (
        <Card>
          <SectionTitle>Andamento trimestrale — Ricavi (milioni {currency})</SectionTitle>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={qData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#475569', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#475569', fontSize: 10 }} tickLine={false} axisLine={false} width={50} tickFormatter={v => `${v}M`} />
              <Tooltip
                contentStyle={{ background: '#0d1117', border: '1px solid #1e2736', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#94a3b8' }}
                formatter={(v, n) => [`${cs}${v}M`, n === 'revenue' ? 'Ricavi' : 'Utile netto']}
              />
              <Bar dataKey="revenue" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Ricavi">
                {qData.map((_, i) => <Cell key={i} fill={i === qData.length - 1 ? '#60a5fa' : '#1e3a5f'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
